import {
	type AggregationsAggregate,
	type SearchHit,
	type SearchResponseBody,
	type SearchTotalHits,
} from '@elastic/elasticsearch/lib/api/types';

import { type WithContext } from '~/interfaces/WithContext';
import { type Loggable } from '~/traits/Loggable';
import { type Wallable } from '~/traits/Wallable';

import { type ComputedAttributes, type ValueBetResult } from '../types';

import { formatInTimeZoneWithLocale } from './formatInTimeZone';

export type OriginalAggregations = {
	unique_bets: UniqueBetsAggregation;
};

export type UniqueBetsAggregation = {
	value: number;
};

export type ValueBetSearchResponseBody = SearchResponseBody<
	ValueBetResult,
	Record<string, AggregationsAggregate>
>;

export type AggregatedValueBetSearchResponseBody = SearchResponseBody<
	ValueBetResult & ComputedAttributes,
	Record<string, AggregationsAggregate>
>;

export type StrippedValueBetSearchResponseBody = SearchResponseBody<
	StrippedValueBetResult,
	Record<string, AggregationsAggregate>
>;

export async function aggregateBetsFromResponse<
	TConfig extends Wallable & WithContext,
>(
	this: Loggable,
	config: TConfig,
	searchResponse: ValueBetSearchResponseBody,
): Promise<
	AggregatedValueBetSearchResponseBody | StrippedValueBetSearchResponseBody
> {
	const aggregations = searchResponse.aggregations as OriginalAggregations;

	const { unique_bets: uniqueBets, ...baseAggregations } = aggregations;

	const aggregatedHits = searchResponse.hits.hits
		.filter(
			(
				hit,
			): hit is SearchHit<ValueBetResult> & {
				_source: ValueBetResult;
				fields: { betId: number[] };
			} => !!hit._source,
		)
		.map(
			// isWalled ?
			// mapHitsToStrippedValueBetResults({ TZ, locale }) :
			mapHitsToValueBetResults(config.user),
		);

	return {
		...searchResponse,
		aggregations: baseAggregations,
		hits: {
			...searchResponse.hits,
			hits: config.isWalled ? aggregatedHits.slice(0, 1) : aggregatedHits,
			total: {
				...(searchResponse.hits.total as SearchTotalHits),
				value: uniqueBets.value,
			},
		},
	};
}

export type StrippedValueBetResult = Omit<
	ValueBetResult,
	| 'aiProbability'
	| 'expectedValue'
	| 'expectedValueStatus'
	| 'oddsAmerican'
	| 'oddsDecimal'
	| 'oddsFractional'
>;

function stripLockedData(result: ValueBetResult): StrippedValueBetResult {
	const {
		aiProbability: _aiProbability,
		expectedValue: _expectedValue,
		expectedValueStatus: _expectedValueStatus,
		oddsAmerican: _oddsAmerican,
		oddsDecimal: _oddsDecimal,
		oddsFractional: _oddsFractional,
		...rest
	} = result;
	return rest;
}

export function mapHitsToStrippedValueBetResults({
	TZ,
	locale,
}: {
	TZ: string;
	locale: string;
}) {
	return (
		hit: SearchHit<ValueBetResult> & {
			_source: ValueBetResult;
			fields: { betId: number[] };
		},
		index: number,
	): SearchHit<StrippedValueBetResult & ComputedAttributes> & {
		_source: StrippedValueBetResult & ComputedAttributes;
	} => {
		return {
			...hit,
			_source: {
				...(index === 0 ? hit._source : stripLockedData(hit._source)),
				subeventStartDate: formatInTimeZoneWithLocale(
					hit._source.subeventStartTime,
					'yyyy-MM-dd',
					TZ,
					locale,
				),
			},
			// Convert betId back to a single number
			fields: {
				betId: hit.fields.betId[0],
			},
		};
	};
}

function mapHitsToValueBetResults({
	TZ,
	locale,
}: {
	TZ: string;
	locale: string;
}) {
	return (
		hit: SearchHit<ValueBetResult> & {
			_source: ValueBetResult;
			fields: { betId: number[] };
		},
	): SearchHit<ValueBetResult & ComputedAttributes> & {
		_source: ValueBetResult & ComputedAttributes;
	} => {
		return {
			...hit,
			_source: {
				...hit._source,
				subeventStartDate: formatInTimeZoneWithLocale(
					hit._source.subeventStartTime,
					'yyyy-MM-dd',
					TZ,
					locale,
				),
			},
			// Convert betId back to a single number
			fields: {
				betId: hit.fields.betId[0],
			},
		};
	};
}
